import { useState } from 'react';

import { countersApi, type CounterTableSummary } from '@/api/queries/counters';
import { Button, Input, Modal, Textarea, useToast } from '@/components/ui';

interface Props {
  table: Pick<CounterTableSummary, 'key' | 'name' | 'description'>;
  onClose: () => void;
  onRenamed?: (newKey: string) => void;
}

export function EditCounterTableModal({ table, onClose, onRenamed }: Props) {
  const update = countersApi.useUpdateMeta();
  const toast = useToast();
  const [name, setName] = useState(table.name);
  const [key, setKey] = useState(table.key);
  const [desc, setDesc] = useState(table.description ?? '');

  const submit = async () => {
    if (!name.trim()) {
      toast.show({ severity: 'error', title: 'Name is required' });
      return;
    }
    const nextKey = key.trim();
    if (!nextKey) {
      toast.show({ severity: 'error', title: 'Key is required' });
      return;
    }
    const renamed = nextKey !== table.key;
    try {
      await update.mutateAsync({
        key: table.key,
        name: name.trim(),
        description: desc,
        newKey: renamed ? nextKey : undefined,
      });
      toast.show({ severity: 'success', title: 'Counter table updated' });
      onClose();
      if (renamed) onRenamed?.(nextKey);
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed to update counter',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Edit counter table"
      subtitle={`counter.${table.key}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>Name</span>
          <Input value={name} onChange={(e) => setName(e.currentTarget.value)} />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>Key</span>
          <Input value={key} onChange={(e) => setKey(e.currentTarget.value.trim())} />
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Lowercase dot-path. Renaming does not update existing references to counter.&lt;key&gt;.
          </span>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>Description</span>
          <Textarea
            value={desc}
            rows={2}
            placeholder="What this counter represents"
            onChange={(e) => setDesc(e.currentTarget.value)}
          />
        </label>
      </div>
    </Modal>
  );
}
